<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';
import ConversationLabelsApi from 'dashboard/api/conversations';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import wootConstants from 'dashboard/constants/globals';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import MessagePreview from 'dashboard/components/widgets/conversation/MessagePreview.vue';
import CardLabels from 'dashboard/components/widgets/conversation/conversationCardComponents/CardLabels.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from 'dashboard/components/widgets/conversation/contextMenu/Index.vue';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';

const KANBAN_VIEW_FILTER_TYPE = 'kanban_view';
const DEFAULT_VIEW_STATUSES = ['all'];
const VALID_KANBAN_STATUSES = ['open', 'pending', 'snoozed', 'all'];
const DEFAULT_WORKFLOW_ROLES = {
  HELP: 'help',
  HUMAN: 'human',
  ASSISTANT: 'assistant',
};
const DEFAULT_WORKFLOW_COLUMNS = [
  {
    role: DEFAULT_WORKFLOW_ROLES.HELP,
    fallbackTitleKey: 'KANBAN_VIEW.DEFAULT_COLUMNS.HELP',
    statusFilter: 'pending',
  },
  {
    role: DEFAULT_WORKFLOW_ROLES.HUMAN,
    fallbackTitleKey: 'KANBAN_VIEW.DEFAULT_COLUMNS.HUMAN',
    statusFilter: ['open', 'snoozed'],
  },
  {
    role: DEFAULT_WORKFLOW_ROLES.ASSISTANT,
    fallbackTitleKey: 'KANBAN_VIEW.DEFAULT_COLUMNS.ASSISTANT',
    statusFilter: 'pending',
  },
];
const DEFAULT_WORKFLOW_ROLE_ALIASES = {
  [DEFAULT_WORKFLOW_ROLES.HELP]: [
    'ajuda',
    'help',
    'ayuda',
    'aide',
    'suporte',
    'support',
    'assistencia',
    'assistance',
  ],
  [DEFAULT_WORKFLOW_ROLES.HUMAN]: [
    'humano',
    'human',
    'agente',
    'atendente',
    'agent',
  ],
  [DEFAULT_WORKFLOW_ROLES.ASSISTANT]: [
    'assistentevirtual',
    'asistentevirtual',
    'assistantvirtual',
    'virtualassistant',
    'assistvirtual',
    'bot',
  ],
};
const PRIORITY_LABEL_KEYS = {
  urgent: 'CONVERSATION.PRIORITY.OPTIONS.URGENT',
  high: 'CONVERSATION.PRIORITY.OPTIONS.HIGH',
  medium: 'CONVERSATION.PRIORITY.OPTIONS.MEDIUM',
  low: 'CONVERSATION.PRIORITY.OPTIONS.LOW',
};
const KANBAN_CONTEXT_MENU_ALLOWED_OPTIONS = [
  'mark-as-read',
  'mark-as-unread',
  'status',
  'snooze',
  'priority',
  'agent',
  'team',
  'open-new-tab',
  'copy-link',
  'delete',
];
const KANBAN_CONTEXT_MENU_HIDDEN_STATUSES = [
  wootConstants.STATUS_TYPE.RESOLVED,
];
const KANBAN_POLLING_INTERVAL_MS = 1500;
const KANBAN_POLLING_STABILIZATION_MS = 1500;
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const sidebarLabels = useMapGetter('labels/getLabelsOnSidebar');
const accountLabels = useMapGetter('labels/getLabels');
const customViewsUiFlags = useMapGetter('customViews/getUIFlags');
const kanbanCustomViews = useMapGetter('customViews/getKanbanCustomViews');

const isLoading = ref(false);
const loadError = ref('');
const columns = ref([]);
const dragState = ref(null);
const activeDropColumnId = ref(null);
const movingConversationId = ref(null);
const contextMenuState = ref({
  conversationId: null,
  x: null,
  y: null,
});

const isViewEditorOpen = ref(false);
const editingViewId = ref(null);
const showDeleteViewModal = ref(false);
const deleteViewCandidate = ref(null);
const viewForm = ref({
  name: '',
  statuses: [...DEFAULT_VIEW_STATUSES],
  columns: [],
});
const pollingIntervalId = ref(null);
const isPollingRefreshInFlight = ref(false);
const lastLocalMutationAt = ref(0);
let unsubscribeConversationStatusMutation = null;

const markLocalMutation = () => {
  lastLocalMutationAt.value = Date.now();
};

const shouldPausePolling = () => {
  return (
    Date.now() - lastLocalMutationAt.value < KANBAN_POLLING_STABILIZATION_MS
  );
};

const normalizeLabels = labelsList => {
  if (!Array.isArray(labelsList)) {
    return [];
  }

  return labelsList.map(label => String(label).trim()).filter(Boolean);
};

const normalizeTitleKey = value => String(value || '').trim().toLowerCase();

const normalizeWorkflowLookupKey = value => {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
};

const humanizeLabelTitle = value => {
  const labelTitle = String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!labelTitle) {
    return '';
  }

  return labelTitle
    .split(' ')
    .map(word => {
      if (!word) {
        return '';
      }

      return `${word.charAt(0).toLocaleUpperCase()}${word.slice(1)}`;
    })
    .join(' ');
};

const matchesWorkflowAlias = (lookupKey, alias) => {
  if (!lookupKey || !alias) {
    return false;
  }

  return (
    lookupKey === alias ||
    lookupKey.startsWith(alias) ||
    lookupKey.endsWith(alias)
  );
};

const detectWorkflowRoleByLabelTitle = title => {
  const lookupKey = normalizeWorkflowLookupKey(title);
  if (!lookupKey) {
    return null;
  }

  for (const columnConfig of DEFAULT_WORKFLOW_COLUMNS) {
    const aliases = DEFAULT_WORKFLOW_ROLE_ALIASES[columnConfig.role] || [];
    if (aliases.some(alias => matchesWorkflowAlias(lookupKey, alias))) {
      return columnConfig.role;
    }
  }

  return null;
};

const normalizeKanbanStatuses = values => {
  const rawValues = Array.isArray(values) ? values : [values];
  const normalizedValues = rawValues
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .filter(value => VALID_KANBAN_STATUSES.includes(value));

  if (!normalizedValues.length || normalizedValues.includes('all')) {
    return [...DEFAULT_VIEW_STATUSES];
  }

  return [...new Set(normalizedValues.filter(value => value !== 'all'))];
};

const statusOptions = computed(() => {
  return [
    {
      id: 'all',
      name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    },
    {
      id: 'open',
      name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    },
    {
      id: 'pending',
      name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    },
    {
      id: 'snoozed',
      name: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    },
  ];
});

const selectedStatusOptions = computed({
  get() {
    const selectedStatuses = normalizeKanbanStatuses(viewForm.value.statuses);
    return statusOptions.value.filter(option =>
      selectedStatuses.includes(option.id)
    );
  },
  set(options) {
    const selectedStatusIds = Array.isArray(options)
      ? options.map(option => option?.id)
      : [];

    const currentStatuses = normalizeKanbanStatuses(viewForm.value.statuses);
    const hasAllSelected = selectedStatusIds.includes('all');
    const selectedStatusIdsWithoutAll = selectedStatusIds.filter(
      status => status !== 'all'
    );

    if (!selectedStatusIds.length) {
      viewForm.value.statuses = [...DEFAULT_VIEW_STATUSES];
      return;
    }

    if (hasAllSelected && selectedStatusIdsWithoutAll.length) {
      viewForm.value.statuses = currentStatuses.includes('all')
        ? normalizeKanbanStatuses(selectedStatusIdsWithoutAll)
        : [...DEFAULT_VIEW_STATUSES];
      return;
    }

    viewForm.value.statuses = normalizeKanbanStatuses(selectedStatusIds);
  },
});

const kanbanViews = computed(() => {
  return (kanbanCustomViews.value || [])
    .map((view, index) => {
      const viewId = Number(view?.id);
      if (!viewId) {
        return null;
      }

      const label = String(view?.name || '').trim();
      const query = view?.query || {};

      return {
        id: viewId,
        key: String(viewId),
        label: label || `${t('SIDEBAR.KANBAN_VIEW')} ${index + 1}`,
        columns: normalizeLabels(query?.columns),
        statuses: normalizeKanbanStatuses(query?.statuses || query?.status),
      };
    })
    .filter(Boolean);
});

const selectedView = computed(() => {
  const viewKey = String(route.params.viewKey || '').trim();
  if (!viewKey) {
    return null;
  }

  return kanbanViews.value.find(view => String(view.id) === viewKey) || null;
});

const isDefaultBoard = computed(() => !selectedView.value);

const currentViewLabel = computed(() => {
  return selectedView.value?.label || t('SIDEBAR.KANBAN_DEFAULT_VIEW');
});

const boardTitle = computed(() => {
  return t('KANBAN_VIEW.BOARD_TITLE', {
    name: currentViewLabel.value,
  });
});

const editActionLabel = computed(() => t('KANBAN_VIEW.ACTIONS.EDIT'));
const deleteActionLabel = computed(() => t('KANBAN_VIEW.ACTIONS.DELETE'));

const statusFilter = computed(() => {
  const selectedStatuses = normalizeKanbanStatuses(selectedView.value?.statuses);

  if (selectedStatuses.includes('all')) {
    return 'all';
  }

  if (selectedStatuses.length === 1) {
    return selectedStatuses[0];
  }

  return selectedStatuses;
});

const allColumnsPool = computed(() => {
  return (sidebarLabels.value || [])
    .map(label => {
      const title = String(label?.title || '').trim();
      return {
        id: Number(label.id),
        title,
        color: label.color,
      };
    })
    .filter(label => !!label.title)
    .sort((a, b) => a.title.localeCompare(b.title));
});

const allAccountLabelsPool = computed(() => {
  return (accountLabels.value || [])
    .map(label => {
      const title = String(label?.title || '').trim();
      return {
        id: Number(label.id),
        title,
        color: label.color,
      };
    })
    .filter(label => !!label.title);
});

const defaultWorkflowLabelByRole = computed(() => {
  const labelsByRole = new Map();

  allAccountLabelsPool.value.forEach(label => {
    const role = detectWorkflowRoleByLabelTitle(label.title);
    if (role && !labelsByRole.has(role)) {
      labelsByRole.set(role, label);
    }
  });

  return labelsByRole;
});

const getWorkflowLabelTitleByRole = role => {
  return defaultWorkflowLabelByRole.value.get(role)?.title || null;
};

const getWorkflowColumnLabelTitleByRole = role => {
  const workflowColumn = defaultBoardColumns.value.find(
    column => column.workflowRole === role
  );

  if (!workflowColumn) {
    return null;
  }

  return workflowColumn.labelTitle || workflowColumn.title || null;
};

const getColumnDisplayTitle = column => {
  if (!isDefaultBoard.value) {
    return `#${column.title}`;
  }

  return column.displayTitle || humanizeLabelTitle(column.title);
};

const defaultBoardColumns = computed(() => {
  return DEFAULT_WORKFLOW_COLUMNS.map(columnConfig => {
    const matchedLabel = defaultWorkflowLabelByRole.value.get(columnConfig.role);
    const resolvedTitle = matchedLabel?.title || t(columnConfig.fallbackTitleKey);

    return {
      id: matchedLabel?.id || `default-${columnConfig.role}`,
      title: resolvedTitle,
      displayTitle: humanizeLabelTitle(resolvedTitle),
      color: matchedLabel?.color || null,
      workflowRole: columnConfig.role,
      labelTitle: matchedLabel?.title || null,
      statusFilter: columnConfig.statusFilter,
    };
  });
});

const visibleLabels = computed(() => {
  if (isDefaultBoard.value) {
    return defaultBoardColumns.value;
  }

  const selectedColumns = selectedView.value?.columns || [];
  if (!selectedColumns.length) {
    return allColumnsPool.value;
  }

  const labelsByTitle = new Map(
    allColumnsPool.value.map(label => [normalizeTitleKey(label.title), label])
  );

  return selectedColumns
    .map(columnTitle => labelsByTitle.get(normalizeTitleKey(columnTitle)))
    .filter(Boolean);
});

const availableColumnsForForm = computed(() => {
  const selectedColumns = new Set(
    normalizeLabels(viewForm.value.columns).map(normalizeTitleKey)
  );

  return allColumnsPool.value.filter(
    column => !selectedColumns.has(normalizeTitleKey(column.title))
  );
});

const isSavingView = computed(() => customViewsUiFlags.value?.isCreating === true);
const isDeletingView = computed(
  () => customViewsUiFlags.value?.isDeleting === true
);
const isViewFormInvalid = computed(() => {
  return (
    !String(viewForm.value.name || '').trim() || !viewForm.value.columns.length
  );
});
const isEditingView = computed(() => Number.isInteger(editingViewId.value));

const activeDeleteViewLabel = computed(() => {
  return deleteViewCandidate.value?.label
    ? ` ${deleteViewCandidate.value.label}?`
    : '';
});

const viewEditorTitle = computed(() => {
  if (isEditingView.value) {
    return t('KANBAN_VIEW.VIEW_EDITOR.TITLE_EDIT');
  }

  return t('KANBAN_VIEW.VIEW_EDITOR.TITLE_CREATE');
});

const viewEditorDescription = computed(() => {
  return t('KANBAN_VIEW.VIEW_EDITOR.DESCRIPTION');
});

const shouldOpenCreateViewFromQuery = computed(() => {
  const createQueryValue = String(route.query.create || '')
    .trim()
    .toLowerCase();
  return createQueryValue === '1' || createQueryValue === 'true';
});

const filterAllowedColumns = columnsList => {
  const availableColumnsByTitle = new Map(
    allColumnsPool.value.map(column => [normalizeTitleKey(column.title), column])
  );

  return normalizeLabels(columnsList)
    .map(column => availableColumnsByTitle.get(normalizeTitleKey(column))?.title)
    .filter(Boolean);
};

const getNormalizedAssignee = assignee => {
  if (!assignee || typeof assignee !== 'object') {
    return null;
  }

  const name =
    assignee.name ||
    assignee.available_name ||
    assignee.availableName ||
    assignee.email ||
    '';

  if (!name) {
    return null;
  }

  return {
    id: assignee.id || assignee.user_id || assignee.userId || name,
    name,
    thumbnail: assignee.thumbnail || assignee.avatar_url || assignee.avatarUrl,
    availabilityStatus:
      assignee.availability_status || assignee.availabilityStatus || null,
  };
};

const getConversationAssignees = conversation => {
  const assigneeCandidates = [
    ...(Array.isArray(conversation?.meta?.assignees)
      ? conversation.meta.assignees
      : []),
    ...(Array.isArray(conversation?.assignees) ? conversation.assignees : []),
    conversation?.meta?.assignee,
    conversation?.assignee,
  ];

  const uniqueAssignees = new Map();

  assigneeCandidates.forEach(assignee => {
    const normalizedAssignee = getNormalizedAssignee(assignee);
    if (!normalizedAssignee) {
      return;
    }

    const key = String(normalizedAssignee.id || normalizedAssignee.name);
    if (!uniqueAssignees.has(key)) {
      uniqueAssignees.set(key, normalizedAssignee);
    }
  });

  return Array.from(uniqueAssignees.values());
};

const getConversationAssigneeLabel = conversation => {
  if (!Array.isArray(conversation.assignees) || !conversation.assignees.length) {
    return '';
  }

  return conversation.assignees.map(assignee => assignee.name).join(', ');
};

const hasUnreadMessages = conversation => Number(conversation.unreadCount || 0) > 0;

const getMessagePreviewClass = conversation => {
  return [
    hasUnreadMessages(conversation)
      ? 'font-medium text-n-slate-12 ltr:pr-4 rtl:pl-4'
      : 'text-n-slate-11',
  ];
};

const getUnreadCountLabel = unreadCount => {
  return unreadCount > 9 ? '9+' : String(unreadCount);
};

const mapConversation = conversation => {
  const latestMessage =
    conversation?.last_non_activity_message ||
    conversation?.last_activity_message ||
    null;

  const sender = conversation?.meta?.sender || {};
  const team = conversation?.meta?.team || conversation?.team || null;

  const senderName =
    sender?.name ||
    sender?.identifier ||
    `#${conversation.display_id || conversation.id}`;

  const assignees = getConversationAssignees(conversation);

  const lastMessage = latestMessage
    ? {
        ...latestMessage,
        attachments: Array.isArray(latestMessage.attachments)
          ? latestMessage.attachments
          : [],
        content_attributes: latestMessage.content_attributes || {},
      }
    : null;

  return {
    id: conversation.id,
    inboxId: conversation.inbox_id,
    displayId: conversation.display_id,
    senderName,
    senderThumbnail: sender?.thumbnail || sender?.avatar_url,
    senderAvailabilityStatus:
      sender?.availabilityStatus || sender?.availability_status || null,
    assignees,
    lastMessage,
    status: String(conversation.status || '').trim(),
    priority: conversation.priority || null,
    snoozedUntil: conversation.snoozed_until || null,
    teamId: team?.id || null,
    teamName: team?.name || '',
    labels: normalizeLabels(conversation.labels),
    unreadCount: Number(conversation.unread_count || 0),
    createdAt:
      conversation.created_at ||
      conversation.timestamp ||
      conversation.last_activity_at ||
      conversation.updated_at,
    lastActivityAt:
      conversation.timestamp ||
      conversation.last_activity_at ||
      conversation.updated_at,
  };
};

const buildConversationQueryParams = ({ column, page }) => {
  const params = {
    page,
    sortBy: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  };

  if (isDefaultBoard.value && column.workflowRole) {
    params.status = column.statusFilter || 'all';
    params.labels = [column.labelTitle || column.title];
    return params;
  }

  params.status = statusFilter.value;
  params.labels = [column.title];
  return params;
};

const fetchConversationsForLabel = async ({ label, page = 1 }) => {
  const params = buildConversationQueryParams({ column: label, page });

  const {
    data: { data: responseData },
  } = await ConversationApi.get(params);

  const conversationList = Array.isArray(responseData?.payload)
    ? responseData.payload
    : [];
  const metaData = responseData?.meta || {};
  const totalCount = Number(metaData?.all_count || conversationList.length);

  return {
    id: label.id,
    title: label.title,
    displayTitle: label.displayTitle || humanizeLabelTitle(label.title),
    color: label.color,
    workflowRole: label.workflowRole || null,
    labelTitle: label.labelTitle || label.title,
    statusFilter: label.statusFilter || null,
    totalCount,
    conversations: conversationList.map(mapConversation),
    currentPage: page,
    hasMore: conversationList.length > 0 && conversationList.length < totalCount,
    isLoadingMore: false,
  };
};

const clearCreateQueryParam = async () => {
  if (!Object.prototype.hasOwnProperty.call(route.query, 'create')) {
    return;
  }

  const nextQuery = { ...route.query };
  delete nextQuery.create;

  await router.replace({
    name: route.name,
    params: route.params,
    query: nextQuery,
  });
};

const maybeOpenCreateViewEditor = async () => {
  if (!shouldOpenCreateViewFromQuery.value || isViewEditorOpen.value) {
    return;
  }

  openCreateViewEditor();
  await clearCreateQueryParam();
};

const loadBoard = async () => {
  isLoading.value = true;
  loadError.value = '';

  try {
    await Promise.all([
      store.dispatch('labels/get'),
      store.dispatch('customViews/get', KANBAN_VIEW_FILTER_TYPE),
    ]);

    if (route.params.viewKey && !selectedView.value) {
      await router.replace(accountScopedRoute('kanban_conversations'));
      return;
    }

    if (!visibleLabels.value.length) {
      columns.value = [];
      await maybeOpenCreateViewEditor();
      return;
    }

    columns.value = await Promise.all(
      visibleLabels.value.map(label =>
        fetchConversationsForLabel({ label, page: 1 })
      )
    );

    await maybeOpenCreateViewEditor();
  } catch {
    loadError.value = t('KANBAN_VIEW.LOAD_ERROR');
  } finally {
    isLoading.value = false;
  }
};

const refreshBoardSilently = async () => {
  if (
    isLoading.value ||
    isPollingRefreshInFlight.value ||
    movingConversationId.value ||
    dragState.value ||
    isViewEditorOpen.value ||
    showDeleteViewModal.value ||
    !columns.value.length ||
    shouldPausePolling()
  ) {
    return;
  }

  if (typeof document !== 'undefined' && document.hidden) {
    return;
  }

  const refreshableColumns = columns.value.filter(column => {
    if (!column || column.isLoadingMore) {
      return false;
    }

    return Number(column.currentPage || 1) <= 1;
  });

  if (!refreshableColumns.length) {
    return;
  }

  isPollingRefreshInFlight.value = true;

  try {
    const refreshedColumns = await Promise.all(
      refreshableColumns.map(async column => ({
        columnId: column.id,
        refreshedColumn: await fetchConversationsForLabel({
          label: { ...column },
          page: 1,
        }),
      }))
    );

    refreshedColumns.forEach(({ columnId, refreshedColumn }) => {
      const targetColumn = columns.value.find(column => column.id === columnId);
      if (!targetColumn) {
        return;
      }

      targetColumn.conversations = refreshedColumn.conversations;
      targetColumn.totalCount = refreshedColumn.totalCount;
      targetColumn.currentPage = 1;
      targetColumn.hasMore = refreshedColumn.hasMore;
    });
  } catch {
    // Keep polling silent to avoid alert noise on transient failures.
  } finally {
    isPollingRefreshInFlight.value = false;
  }
};

const stopBoardPolling = () => {
  if (pollingIntervalId.value === null) {
    return;
  }

  clearInterval(pollingIntervalId.value);
  pollingIntervalId.value = null;
};

const startBoardPolling = () => {
  if (pollingIntervalId.value !== null) {
    return;
  }

  pollingIntervalId.value = setInterval(() => {
    refreshBoardSilently();
  }, KANBAN_POLLING_INTERVAL_MS);
};

const resetViewForm = () => {
  editingViewId.value = null;
  viewForm.value = {
    name: '',
    statuses: [...DEFAULT_VIEW_STATUSES],
    columns: [],
  };
};

const openCreateViewEditor = () => {
  const suggestedName = `${t('SIDEBAR.KANBAN_VIEW')} ${kanbanViews.value.length + 1}`;

  editingViewId.value = null;
  viewForm.value = {
    name: suggestedName,
    statuses: normalizeKanbanStatuses(selectedView.value?.statuses),
    columns: [],
  };
  isViewEditorOpen.value = true;
};

const openEditViewEditor = () => {
  if (!selectedView.value) {
    return;
  }

  editingViewId.value = selectedView.value.id;
  viewForm.value = {
    name: selectedView.value.label,
    statuses: normalizeKanbanStatuses(selectedView.value.statuses),
    columns: filterAllowedColumns(selectedView.value.columns),
  };
  isViewEditorOpen.value = true;
};

const closeViewEditor = () => {
  isViewEditorOpen.value = false;
  resetViewForm();
};

const addColumnToViewForm = title => {
  const normalizedTitle = String(title || '').trim();
  if (!normalizedTitle) {
    return;
  }

  const existingColumns = new Set(
    viewForm.value.columns.map(column => normalizeTitleKey(column))
  );
  if (existingColumns.has(normalizeTitleKey(normalizedTitle))) {
    return;
  }

  viewForm.value.columns = [...viewForm.value.columns, normalizedTitle];
};

const removeColumnFromViewForm = index => {
  viewForm.value.columns = viewForm.value.columns.filter(
    (_column, columnIndex) => columnIndex !== index
  );
};

const moveColumnInViewForm = (index, direction) => {
  const targetIndex = index + direction;
  if (
    targetIndex < 0 ||
    targetIndex >= viewForm.value.columns.length ||
    targetIndex === index
  ) {
    return;
  }

  const nextColumns = [...viewForm.value.columns];
  const [movedColumn] = nextColumns.splice(index, 1);
  nextColumns.splice(targetIndex, 0, movedColumn);
  viewForm.value.columns = nextColumns;
};

const buildViewPayload = () => {
  const statuses = normalizeKanbanStatuses(viewForm.value.statuses);

  return {
    name: String(viewForm.value.name || '').trim(),
    filter_type: KANBAN_VIEW_FILTER_TYPE,
    query: {
      columns: filterAllowedColumns(viewForm.value.columns),
      statuses,
      ...(statuses.length === 1 ? { status: statuses[0] } : {}),
    },
  };
};

const saveView = async () => {
  if (isViewFormInvalid.value || isSavingView.value) {
    useAlert(t('KANBAN_VIEW.VIEW_EDITOR.VALIDATION_ERROR'));
    return;
  }

  const payload = buildViewPayload();

  try {
    if (editingViewId.value) {
      await store.dispatch('customViews/update', {
        id: editingViewId.value,
        ...payload,
      });

      await router.push(
        accountScopedRoute('kanban_view_conversations', {
          viewKey: editingViewId.value,
        })
      );
    } else {
      const response = await store.dispatch('customViews/create', payload);
      const createdViewId = Number(response?.data?.id);

      useAlert(t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.SUCCESS_MESSAGE'));
      if (createdViewId) {
        await router.push(
          accountScopedRoute('kanban_view_conversations', {
            viewKey: createdViewId,
          })
        );
      }
    }

    closeViewEditor();
    await loadBoard();
  } catch {
    useAlert(t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.ERROR_MESSAGE'));
  }
};

const promptDeleteCurrentView = () => {
  if (!selectedView.value) {
    return;
  }

  deleteViewCandidate.value = selectedView.value;
  showDeleteViewModal.value = true;
};

const closeDeleteViewModal = () => {
  showDeleteViewModal.value = false;
  deleteViewCandidate.value = null;
};

const deleteCurrentView = async () => {
  if (!deleteViewCandidate.value || isDeletingView.value) {
    return;
  }

  const deletedViewId = deleteViewCandidate.value.id;

  try {
    await store.dispatch('customViews/delete', {
      id: deletedViewId,
      filterType: KANBAN_VIEW_FILTER_TYPE,
    });

    closeDeleteViewModal();
    useAlert(t('FILTER.CUSTOM_VIEWS.DELETE.API_FOLDERS.SUCCESS_MESSAGE'));

    if (String(route.params.viewKey || '') === String(deletedViewId)) {
      await router.push(accountScopedRoute('kanban_conversations'));
    }

    await loadBoard();
  } catch {
    useAlert(t('FILTER.CUSTOM_VIEWS.DELETE.API_FOLDERS.ERROR_MESSAGE'));
  }
};

const getConversationStatusLabel = status => {
  if (status === 'open') {
    return t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT');
  }

  if (status === 'pending') {
    return t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT');
  }

  if (status === 'resolved') {
    return t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT');
  }

  if (status === 'snoozed') {
    return t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT');
  }

  return t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT');
};

const getConversationStatusBadgeClass = status => {
  if (status === 'open') {
    return 'bg-n-teal-2 text-n-teal-11';
  }

  if (status === 'pending') {
    return 'bg-n-amber-2 text-n-amber-11';
  }

  if (status === 'snoozed') {
    return 'bg-n-blue-2 text-n-blue-11';
  }

  if (status === 'resolved') {
    return 'bg-n-ruby-2 text-n-ruby-11';
  }

  return 'bg-n-alpha-2 text-n-slate-11';
};

const getSnoozedConditionLabel = conversation => {
  if (conversation.snoozedUntil) {
    const reopenTime = snoozedReopenTime(conversation.snoozedUntil);
    if (reopenTime) {
      return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${reopenTime}`;
    }
  }

  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
};

const getPriorityLabel = priority => {
  const priorityKey = PRIORITY_LABEL_KEYS[String(priority || '').toLowerCase()];
  return priorityKey ? t(priorityKey) : '';
};

const normalizeHexColor = value => {
  const color = String(value || '').trim();
  if (!color.startsWith('#')) {
    return null;
  }

  const hexValue = color.slice(1);
  const normalizedHex =
    hexValue.length === 3
      ? hexValue
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : hexValue;

  if (!/^[0-9a-fA-F]{6}$/.test(normalizedHex)) {
    return null;
  }

  return `#${normalizedHex}`;
};

const isLightColor = value => {
  const normalizedHex = normalizeHexColor(value);
  if (!normalizedHex) {
    return false;
  }

  const red = parseInt(normalizedHex.slice(1, 3), 16);
  const green = parseInt(normalizedHex.slice(3, 5), 16);
  const blue = parseInt(normalizedHex.slice(5, 7), 16);
  const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
  return luminance >= 0.65;
};

const getColumnHeaderStyle = column => {
  const color = String(column?.color || '').trim();
  if (!color) {
    return {};
  }

  return {
    backgroundColor: color,
  };
};

const getColumnHeaderClass = column => {
  if (!column?.color) {
    return 'bg-n-alpha-1 text-n-slate-12';
  }

  return isLightColor(column.color) ? 'text-n-slate-12' : 'text-white';
};

const getColumnCounterClass = column => {
  if (!column?.color) {
    return 'bg-n-alpha-2 text-n-slate-11';
  }

  return isLightColor(column.color)
    ? 'bg-black/10 text-n-slate-12'
    : 'bg-white/20 text-white';
};

const isConversationContextMenuOpen = computed(() => {
  return Number.isInteger(contextMenuState.value.conversationId);
});

const contextMenuConversation = computed(() => {
  if (!contextMenuState.value.conversationId) {
    return null;
  }

  for (const column of columns.value) {
    const conversation = column.conversations.find(
      item => item.id === contextMenuState.value.conversationId
    );
    if (conversation) {
      return conversation;
    }
  }

  return null;
});

const closeConversationContextMenu = () => {
  contextMenuState.value = {
    conversationId: null,
    x: null,
    y: null,
  };
};

const openConversationContextMenu = (event, conversationId) => {
  if (movingConversationId.value) {
    return;
  }

  event.preventDefault();
  contextMenuState.value = {
    conversationId,
    x: event.pageX || event.clientX,
    y: event.pageY || event.clientY,
  };
};

const getConversationPath = conversationId => {
  return router.resolve(
    accountScopedRoute('inbox_conversation', {
      conversation_id: conversationId,
    })
  ).href;
};

const matchesStatusFilter = (conversationStatus, statusRule) => {
  const normalizedStatus = String(conversationStatus || '').trim();

  if (!statusRule || statusRule === 'all') {
    return true;
  }

  if (Array.isArray(statusRule)) {
    return statusRule.includes(normalizedStatus);
  }

  return normalizedStatus === String(statusRule || '').trim();
};

const doesConversationBelongToColumn = ({ conversation, column }) => {
  if (!conversation || !column) {
    return false;
  }

  const labelTitle =
    isDefaultBoard.value && column.workflowRole
      ? column.labelTitle || null
      : column.title;
  const statusRule =
    isDefaultBoard.value && column.workflowRole
      ? column.statusFilter || 'all'
      : statusFilter.value;
  const hasMatchingLabel =
    isDefaultBoard.value && column.workflowRole
      ? labelTitle
        ? hasLabel({ labels: conversation.labels, labelTitle })
        : hasWorkflowRoleLabel({
            labels: conversation.labels,
            role: column.workflowRole,
          })
      : hasLabel({ labels: conversation.labels, labelTitle });

  return hasMatchingLabel && matchesStatusFilter(conversation.status, statusRule);
};

const getConversationFromAnyColumn = conversationId => {
  for (const column of columns.value) {
    const conversation = column.conversations.find(
      item => item.id === conversationId
    );

    if (conversation) {
      return conversation;
    }
  }

  return null;
};

const getConversationColumnId = conversationId => {
  const matchedColumn = columns.value.find(column =>
    column.conversations.some(item => item.id === conversationId)
  );

  return matchedColumn?.id || null;
};

const syncConversationAcrossColumns = ({
  conversationId,
  updater,
  prioritizeColumnId = null,
}) => {
  const baseConversation = getConversationFromAnyColumn(conversationId);
  if (!baseConversation) {
    return null;
  }

  const updatedConversation = updater({ ...baseConversation });

  columns.value.forEach(column => {
    const conversationIndex = column.conversations.findIndex(
      item => item.id === conversationId
    );
    const shouldForceAppear =
      prioritizeColumnId !== null && column.id === prioritizeColumnId;
    const shouldAppear =
      shouldForceAppear ||
      doesConversationBelongToColumn({
        conversation: updatedConversation,
        column,
      });

    if (shouldAppear) {
      const nextConversation = { ...updatedConversation };

      if (conversationIndex === -1) {
        column.conversations.unshift(nextConversation);
        column.totalCount = Number(column.totalCount || 0) + 1;
        return;
      }

      column.conversations.splice(conversationIndex, 1);

      if (prioritizeColumnId && column.id === prioritizeColumnId) {
        column.conversations.unshift(nextConversation);
      } else {
        column.conversations.splice(conversationIndex, 0, nextConversation);
      }
      return;
    }

    if (conversationIndex !== -1) {
      column.conversations.splice(conversationIndex, 1);
      column.totalCount = Math.max(0, Number(column.totalCount || 0) - 1);
    }
  });

  markLocalMutation();

  return updatedConversation;
};

const updateConversationInColumns = (conversationId, updater) => {
  let firstUpdatedConversation = null;

  for (const column of columns.value) {
    const conversationIndex = column.conversations.findIndex(
      item => item.id === conversationId
    );

    if (conversationIndex === -1) {
      continue;
    }

    const currentConversation = column.conversations[conversationIndex];
    const nextConversation = updater({ ...currentConversation });
    column.conversations.splice(conversationIndex, 1, nextConversation);

    if (!firstUpdatedConversation) {
      firstUpdatedConversation = nextConversation;
    }
  }

  if (firstUpdatedConversation) {
    markLocalMutation();
  }

  return firstUpdatedConversation;
};

const removeConversationFromColumns = conversationId => {
  let removedAnyConversation = false;

  for (const column of columns.value) {
    const conversationIndex = column.conversations.findIndex(
      item => item.id === conversationId
    );

    if (conversationIndex === -1) {
      continue;
    }

    column.conversations.splice(conversationIndex, 1);
    column.totalCount = Math.max(0, Number(column.totalCount || 0) - 1);
    removedAnyConversation = true;
  }

  if (removedAnyConversation) {
    markLocalMutation();
  }

  return removedAnyConversation;
};

const getDefaultBoardLabelTitleForRole = role => {
  return getWorkflowColumnLabelTitleByRole(role) || getWorkflowLabelTitleByRole(role);
};

const getNextLabelsForDefaultBoardStatus = ({ labels, nextStatus }) => {
  if (!isDefaultBoard.value) {
    return normalizeLabels(labels);
  }

  const normalizedStatus = String(nextStatus || '').trim();
  const normalizedLabels = normalizeLabels(labels);

  if (normalizedStatus === 'pending') {
    return addLabel({
      labels: removeWorkflowRoleLabels({
        labels: normalizedLabels,
        role: DEFAULT_WORKFLOW_ROLES.HELP,
      }),
      labelTitle: getDefaultBoardLabelTitleForRole(DEFAULT_WORKFLOW_ROLES.ASSISTANT),
    });
  }

  if (normalizedStatus === 'open' || normalizedStatus === 'snoozed') {
    return addLabel({
      labels: normalizedLabels,
      labelTitle: getDefaultBoardLabelTitleForRole(DEFAULT_WORKFLOW_ROLES.HUMAN),
    });
  }

  return normalizedLabels;
};

const syncConversationStatusFromMutation = payload => {
  const conversationId = Number(payload?.conversationId);
  const nextStatus = String(payload?.status || '').trim();

  if (!conversationId || !nextStatus) {
    return;
  }

  const sourceConversation = getConversationFromAnyColumn(conversationId);
  if (!sourceConversation) {
    return;
  }

  const nextSnoozedUntil =
    nextStatus === 'snoozed' ? payload?.snoozedUntil || null : null;
  const nextLabels = getNextLabelsForDefaultBoardStatus({
    labels: sourceConversation.labels,
    nextStatus,
  });

  syncConversationAcrossColumns({
    conversationId,
    updater: conversation => ({
      ...conversation,
      labels: nextLabels,
      status: nextStatus,
      snoozedUntil: nextSnoozedUntil,
    }),
  });
};

const updateConversationStatusFromContextMenu = async (status, snoozedUntil) => {
  if (!contextMenuConversation.value) {
    return;
  }

  const contextConversation = contextMenuConversation.value;
  const conversationId = contextConversation.id;
  const nextStatus = String(status || '').trim();
  const nextSnoozedUntil = nextStatus === 'snoozed' ? snoozedUntil : null;
  markLocalMutation();
  const sourceColumnId = getConversationColumnId(conversationId);
  let rollbackSnapshot = null;

  try {
    const existingLabels = await resolveConversationLabels(contextConversation);
    const nextLabels = getNextLabelsForDefaultBoardStatus({
      labels: existingLabels,
      nextStatus,
    });
    const labelsChanged = !areSameLabelSet(existingLabels, nextLabels);

    rollbackSnapshot = {
      conversationId,
      targetColumnId: sourceColumnId,
      nextLabels: normalizeLabels(contextConversation.labels),
      nextStatus: contextConversation.status,
      nextSnoozedUntil: contextConversation.snoozedUntil || null,
    };

    syncConversationAcrossColumns({
      conversationId,
      updater: conversation => ({
        ...conversation,
        labels: labelsChanged ? nextLabels : conversation.labels,
        status: nextStatus,
        snoozedUntil: nextSnoozedUntil,
      }),
    });

    if (labelsChanged) {
      await ConversationLabelsApi.updateLabels(conversationId, nextLabels);
    }

    const {
      data: {
        payload: {
          current_status: updatedStatus,
          snoozed_until: updatedSnoozedUntil,
        } = {},
      } = {},
    } = await ConversationApi.toggleStatus({
      conversationId,
      status: nextStatus,
      snoozedUntil: nextSnoozedUntil,
    });

    syncConversationAcrossColumns({
      conversationId,
      updater: conversation => ({
        ...conversation,
        labels: labelsChanged ? nextLabels : conversation.labels,
        status: String(updatedStatus || nextStatus).trim(),
        snoozedUntil:
          String(updatedStatus || nextStatus).trim() === 'snoozed'
            ? updatedSnoozedUntil || nextSnoozedUntil
            : null,
      }),
    });

    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  } catch {
    if (rollbackSnapshot) {
      moveConversationLocally({
        conversationId: rollbackSnapshot.conversationId,
        targetColumnId: rollbackSnapshot.targetColumnId,
        nextLabels: rollbackSnapshot.nextLabels,
        nextStatus: rollbackSnapshot.nextStatus,
        nextSnoozedUntil: rollbackSnapshot.nextSnoozedUntil,
      });
    }
    useAlert(t('KANBAN_VIEW.LOAD_ERROR'));
  } finally {
    closeConversationContextMenu();
  }
};

const markConversationAsRead = async () => {
  if (!contextMenuConversation.value) {
    return;
  }

  markLocalMutation();

  try {
    await store.dispatch('markMessagesRead', {
      id: contextMenuConversation.value.id,
    });

    updateConversationInColumns(contextMenuConversation.value.id, conversation => ({
      ...conversation,
      unreadCount: 0,
    }));
  } catch {
    useAlert(t('KANBAN_VIEW.LOAD_ERROR'));
  } finally {
    closeConversationContextMenu();
  }
};

const markConversationAsUnread = async () => {
  if (!contextMenuConversation.value) {
    return;
  }

  markLocalMutation();

  try {
    await store.dispatch('markMessagesUnread', {
      id: contextMenuConversation.value.id,
    });

    updateConversationInColumns(contextMenuConversation.value.id, conversation => ({
      ...conversation,
      unreadCount: conversation.unreadCount > 0 ? conversation.unreadCount : 1,
    }));
  } catch {
    useAlert(t('KANBAN_VIEW.LOAD_ERROR'));
  } finally {
    closeConversationContextMenu();
  }
};

const assignConversationPriority = async priority => {
  if (!contextMenuConversation.value) {
    return;
  }

  const nextPriority = priority || null;
  markLocalMutation();

  try {
    await store.dispatch('setCurrentChatPriority', {
      priority: nextPriority,
      conversationId: contextMenuConversation.value.id,
    });
    await store.dispatch('assignPriority', {
      conversationId: contextMenuConversation.value.id,
      priority: nextPriority,
    });

    updateConversationInColumns(contextMenuConversation.value.id, conversation => ({
      ...conversation,
      priority: nextPriority,
    }));

    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority: nextPriority || t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
        conversationId: contextMenuConversation.value.id,
      })
    );
  } catch {
    useAlert(t('KANBAN_VIEW.LOAD_ERROR'));
  } finally {
    closeConversationContextMenu();
  }
};

const assignConversationAgent = async agent => {
  if (!contextMenuConversation.value) {
    return;
  }

  const conversationId = contextMenuConversation.value.id;
  const nextAssigneeId = agent?.id ?? null;
  const isUnassigned = nextAssigneeId === null;
  const normalizedAssignee = getNormalizedAssignee(agent);
  markLocalMutation();

  try {
    await store.dispatch('bulkActions/process', {
      type: 'Conversation',
      ids: [conversationId],
      fields: {
        assignee_id: nextAssigneeId,
      },
    });

    updateConversationInColumns(conversationId, conversation => ({
      ...conversation,
      assignees:
        isUnassigned || !normalizedAssignee ? [] : [normalizedAssignee],
    }));

    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL', {
          agentName: agent?.name || t('CONVERSATION.PRIORITY.OPTIONS.NONE'),
        conversationId,
      })
    );
  } catch {
    useAlert(t('BULK_ACTION.ASSIGN_FAILED'));
  } finally {
    closeConversationContextMenu();
  }
};

const assignConversationTeam = async team => {
  if (!contextMenuConversation.value) {
    return;
  }

  const conversationId = contextMenuConversation.value.id;
  markLocalMutation();

  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });

    updateConversationInColumns(conversationId, conversation => ({
      ...conversation,
      teamId: team.id,
      teamName: team.name,
    }));

    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  } finally {
    closeConversationContextMenu();
  }
};

const deleteConversationFromContextMenu = async () => {
  if (!contextMenuConversation.value) {
    return;
  }

  markLocalMutation();

  try {
    await store.dispatch('deleteConversation', contextMenuConversation.value.id);
    removeConversationFromColumns(contextMenuConversation.value.id);
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  } finally {
    closeConversationContextMenu();
  }
};

const onDragStart = (event, conversation, column) => {
  closeConversationContextMenu();

  dragState.value = {
    conversationId: conversation.id,
    sourceColumnId: column.id,
  };
  activeDropColumnId.value = column.id;

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(conversation.id));
  }
};

const clearDragState = () => {
  dragState.value = null;
  activeDropColumnId.value = null;
};

const onColumnDragOver = (event, columnId) => {
  if (!dragState.value) {
    return;
  }

  event.preventDefault();
  activeDropColumnId.value = columnId;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
};

const getColumnById = columnId => {
  return columns.value.find(column => column.id === columnId);
};

const getConversationFromColumn = (columnId, conversationId) => {
  const column = getColumnById(columnId);
  if (!column) {
    return null;
  }

  return (
    column.conversations.find(conversation => conversation.id === conversationId) ||
    null
  );
};

const resolveConversationLabels = async conversation => {
  const existingLabels = normalizeLabels(conversation.labels);
  if (existingLabels.length) {
    return existingLabels;
  }

  const {
    data: { payload },
  } = await ConversationLabelsApi.getLabels(conversation.id);
  return normalizeLabels(payload);
};

const computeNextLabels = ({ existingLabels, sourceLabel, targetLabel }) => {
  const labelsWithoutSource = existingLabels.filter(
    label => label !== sourceLabel
  );
  return [...new Set([...labelsWithoutSource, targetLabel])];
};

const hasLabel = ({ labels, labelTitle }) => {
  if (!labelTitle) {
    return false;
  }

  const targetKey = normalizeTitleKey(labelTitle);
  return normalizeLabels(labels).some(label => normalizeTitleKey(label) === targetKey);
};

const hasWorkflowRoleLabel = ({ labels, role }) => {
  return normalizeLabels(labels).some(
    label => detectWorkflowRoleByLabelTitle(label) === role
  );
};

const addLabel = ({ labels, labelTitle }) => {
  if (!labelTitle) {
    return normalizeLabels(labels);
  }

  const normalizedLabels = normalizeLabels(labels);
  if (hasLabel({ labels: normalizedLabels, labelTitle })) {
    return normalizedLabels;
  }

  return [...normalizedLabels, labelTitle];
};

const removeLabel = ({ labels, labelTitle }) => {
  if (!labelTitle) {
    return normalizeLabels(labels);
  }

  const targetKey = normalizeTitleKey(labelTitle);
  return normalizeLabels(labels).filter(label => normalizeTitleKey(label) !== targetKey);
};

const removeWorkflowRoleLabels = ({ labels, role }) => {
  return normalizeLabels(labels).filter(
    label => detectWorkflowRoleByLabelTitle(label) !== role
  );
};

const areSameLabelSet = (labelsA, labelsB) => {
  const normalizedA = [...new Set(normalizeLabels(labelsA).map(normalizeTitleKey))].sort();
  const normalizedB = [...new Set(normalizeLabels(labelsB).map(normalizeTitleKey))].sort();

  if (normalizedA.length !== normalizedB.length) {
    return false;
  }

  return normalizedA.every((label, index) => label === normalizedB[index]);
};

const buildDefaultBoardDropUpdate = ({ sourceConversation, targetColumn, existingLabels }) => {
  const targetRole = targetColumn.workflowRole;
  if (!targetRole) {
    return null;
  }

  const helpLabelTitle =
    getWorkflowColumnLabelTitleByRole(DEFAULT_WORKFLOW_ROLES.HELP) ||
    getWorkflowLabelTitleByRole(DEFAULT_WORKFLOW_ROLES.HELP);
  const humanLabelTitle =
    getWorkflowColumnLabelTitleByRole(DEFAULT_WORKFLOW_ROLES.HUMAN) ||
    getWorkflowLabelTitleByRole(DEFAULT_WORKFLOW_ROLES.HUMAN);
  const assistantLabelTitle =
    getWorkflowColumnLabelTitleByRole(DEFAULT_WORKFLOW_ROLES.ASSISTANT) ||
    getWorkflowLabelTitleByRole(
    DEFAULT_WORKFLOW_ROLES.ASSISTANT
  );

  const sourceStatus = String(sourceConversation.status || '').trim();

  if (targetRole === DEFAULT_WORKFLOW_ROLES.HELP) {
    const canMoveToHelpColumn =
      sourceStatus === 'pending' &&
      Boolean(helpLabelTitle) &&
      hasWorkflowRoleLabel({
        labels: existingLabels,
        role: DEFAULT_WORKFLOW_ROLES.ASSISTANT,
      });

    if (!canMoveToHelpColumn) {
      return {
        isAllowed: false,
      };
    }

    return {
      isAllowed: true,
      nextStatus: 'pending',
      nextSnoozedUntil: null,
      nextLabels: addLabel({ labels: existingLabels, labelTitle: helpLabelTitle }),
    };
  }

  if (targetRole === DEFAULT_WORKFLOW_ROLES.HUMAN) {
    return {
      isAllowed: true,
      nextStatus: 'open',
      nextSnoozedUntil: null,
      nextLabels: addLabel({ labels: existingLabels, labelTitle: humanLabelTitle }),
    };
  }

  if (targetRole === DEFAULT_WORKFLOW_ROLES.ASSISTANT) {
    return {
      isAllowed: true,
      nextStatus: 'pending',
      nextSnoozedUntil: null,
      nextLabels: addLabel({
        labels: removeWorkflowRoleLabels({
          labels: existingLabels,
          role: DEFAULT_WORKFLOW_ROLES.HELP,
        }),
        labelTitle: assistantLabelTitle,
      }),
    };
  }

  return {
    isAllowed: false,
  };
};

const moveConversationLocally = ({
  conversationId,
  targetColumnId,
  nextLabels,
  nextStatus,
  nextSnoozedUntil,
}) => {
  syncConversationAcrossColumns({
    conversationId,
    prioritizeColumnId: targetColumnId,
    updater: conversation => ({
      ...conversation,
      labels: nextLabels || conversation.labels,
      status: nextStatus || conversation.status,
      snoozedUntil:
        nextSnoozedUntil !== undefined
          ? nextSnoozedUntil
          : conversation.snoozedUntil,
    }),
  });
};

const onColumnDrop = async (event, targetColumn) => {
  event.preventDefault();

  if (!dragState.value || movingConversationId.value) {
    return;
  }

  const { conversationId, sourceColumnId } = dragState.value;
  if (sourceColumnId === targetColumn.id) {
    clearDragState();
    return;
  }

  const sourceColumn = getColumnById(sourceColumnId);
  const sourceConversation = getConversationFromColumn(
    sourceColumnId,
    conversationId
  );
  if (!sourceColumn || !sourceConversation) {
    clearDragState();
    return;
  }

  movingConversationId.value = conversationId;
  let rollbackSnapshot = null;
  markLocalMutation();

  try {
    const existingLabels = await resolveConversationLabels(sourceConversation);

    if (isDefaultBoard.value && targetColumn.workflowRole) {
      const workflowUpdate = buildDefaultBoardDropUpdate({
        sourceConversation,
        targetColumn,
        existingLabels,
      });

      if (!workflowUpdate?.isAllowed) {
        useAlert(t('KANBAN_VIEW.MOVE_ERROR'));
        return;
      }

      const { nextLabels, nextStatus, nextSnoozedUntil } = workflowUpdate;
      const labelsChanged = !areSameLabelSet(existingLabels, nextLabels);
      const statusChanged =
        String(sourceConversation.status || '').trim() !==
          String(nextStatus || '').trim() ||
        (sourceConversation.snoozedUntil || null) !== (nextSnoozedUntil || null);

      rollbackSnapshot = {
        targetColumnId: sourceColumnId,
        nextLabels: normalizeLabels(sourceConversation.labels),
        nextStatus: sourceConversation.status,
        nextSnoozedUntil: sourceConversation.snoozedUntil || null,
      };

      moveConversationLocally({
        conversationId,
        sourceColumnId,
        targetColumnId: targetColumn.id,
        nextLabels,
        nextStatus,
        nextSnoozedUntil,
      });

      if (targetColumn.workflowRole === DEFAULT_WORKFLOW_ROLES.ASSISTANT) {
        if (labelsChanged) {
          await ConversationLabelsApi.updateLabels(conversationId, nextLabels);
        }
        if (statusChanged) {
          await store.dispatch('toggleStatus', {
            conversationId,
            status: nextStatus,
            snoozedUntil: nextSnoozedUntil,
          });
        }
      } else {
        if (labelsChanged) {
          await ConversationLabelsApi.updateLabels(conversationId, nextLabels);
        }
        if (statusChanged) {
          await store.dispatch('toggleStatus', {
            conversationId,
            status: nextStatus,
            snoozedUntil: nextSnoozedUntil,
          });
        }
      }
      return;
    }

    const nextLabels = computeNextLabels({
      existingLabels,
      sourceLabel: sourceColumn.title,
      targetLabel: targetColumn.title,
    });

    rollbackSnapshot = {
      targetColumnId: sourceColumnId,
      nextLabels: normalizeLabels(sourceConversation.labels),
      nextStatus: sourceConversation.status,
      nextSnoozedUntil: sourceConversation.snoozedUntil || null,
    };

    moveConversationLocally({
      conversationId,
      sourceColumnId,
      targetColumnId: targetColumn.id,
      nextLabels,
    });

    await ConversationLabelsApi.updateLabels(conversationId, nextLabels);
  } catch {
    if (rollbackSnapshot) {
      moveConversationLocally({
        conversationId,
        sourceColumnId,
        targetColumnId: rollbackSnapshot.targetColumnId,
        nextLabels: rollbackSnapshot.nextLabels,
        nextStatus: rollbackSnapshot.nextStatus,
        nextSnoozedUntil: rollbackSnapshot.nextSnoozedUntil,
      });
    }
    useAlert(t('KANBAN_VIEW.MOVE_ERROR'));
  } finally {
    movingConversationId.value = null;
    clearDragState();
  }
};

const onConversationCardClick = conversationId => {
  if (movingConversationId.value) {
    return;
  }

  openConversation(conversationId);
};

const loadMoreConversations = async columnId => {
  const column = getColumnById(columnId);
  if (!column || column.isLoadingMore || !column.hasMore) {
    return;
  }

  column.isLoadingMore = true;
  const nextPage = Number(column.currentPage || 1) + 1;

  try {
    const response = await fetchConversationsForLabel({
      label: { ...column },
      page: nextPage,
    });

    const existingConversationIds = new Set(
      column.conversations.map(conversation => conversation.id)
    );

    const nextConversations = response.conversations.filter(
      conversation => !existingConversationIds.has(conversation.id)
    );

    column.conversations = [...column.conversations, ...nextConversations];
    column.totalCount = response.totalCount;
    column.currentPage = nextPage;
    column.hasMore =
      response.conversations.length > 0 &&
      column.conversations.length < column.totalCount;
  } catch {
    useAlert(t('KANBAN_VIEW.LOAD_ERROR'));
  } finally {
    column.isLoadingMore = false;
  }
};

const openConversation = conversationId => {
  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: conversationId,
    })
  );
};

onMounted(async () => {
  await loadBoard();
  startBoardPolling();

  unsubscribeConversationStatusMutation = store.subscribe(mutation => {
    const mutationType = String(mutation?.type || '');
    if (!mutationType.endsWith('CHANGE_CONVERSATION_STATUS')) {
      return;
    }

    syncConversationStatusFromMutation(mutation?.payload);
  });
});

onUnmounted(() => {
  stopBoardPolling();

  if (typeof unsubscribeConversationStatusMutation === 'function') {
    unsubscribeConversationStatusMutation();
    unsubscribeConversationStatusMutation = null;
  }
});

watch(
  () => route.params.viewKey,
  () => {
    closeConversationContextMenu();
    loadBoard();
  }
);

watch(
  () => route.query.create,
  () => {
    maybeOpenCreateViewEditor();
  }
);
</script>

<template>
  <section class="flex h-full min-h-0 w-full flex-col overflow-hidden bg-n-surface-1">
    <header
      class="shrink-0 border-b border-n-weak px-4 py-4 md:px-5"
    >
      <div class="flex flex-wrap items-center gap-2">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ boardTitle }}
        </h1>

        <div class="flex items-center gap-2">
          <NextButton
            v-if="selectedView"
            v-tooltip.top="editActionLabel"
            xs
            faded
            slate
            class="!rounded-full"
            icon="i-lucide-pen-line"
            :title="editActionLabel"
            :aria-label="editActionLabel"
            @click="openEditViewEditor"
          />
          <NextButton
            v-if="selectedView"
            v-tooltip.top="deleteActionLabel"
            xs
            faded
            ruby
            class="!rounded-full"
            icon="i-lucide-trash-2"
            :title="deleteActionLabel"
            :aria-label="deleteActionLabel"
            @click="promptDeleteCurrentView"
          />
          <NextButton
            v-tooltip.top="t('KANBAN_VIEW.REFRESH')"
            xs
            faded
            slate
            class="!rounded-full"
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            :title="t('KANBAN_VIEW.REFRESH')"
            :aria-label="t('KANBAN_VIEW.REFRESH')"
            @click="loadBoard"
          />
        </div>
      </div>
    </header>

    <div class="flex-1 min-h-0 overflow-hidden px-4 py-4 md:px-5 md:py-4">
      <p v-if="loadError" class="mb-3 text-sm text-n-ruby-9">
        {{ loadError }}
      </p>

      <div v-if="isLoading" class="grid h-full min-h-[10rem] place-items-center">
        <Spinner :size="24" class="text-n-slate-10" />
      </div>

      <p
        v-else-if="!visibleLabels.length"
        class="rounded-lg border border-dashed border-n-weak p-4 text-sm text-n-slate-11"
      >
        {{ t('KANBAN_VIEW.EMPTY_LABELS') }}
      </p>

      <div v-else class="flex h-full min-h-0 flex-col overflow-hidden">
        <div class="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
          <div class="flex h-full min-w-max items-stretch gap-4 pb-2">
            <section
              v-for="column in columns"
              :key="column.id"
              class="flex h-full w-80 max-w-80 shrink-0 flex-col overflow-hidden rounded-xl border border-n-weak bg-n-alpha-1"
              :class="{
                'border-n-slate-6 bg-n-alpha-2': activeDropColumnId === column.id,
              }"
            >
              <header
                class="flex shrink-0 items-center justify-between gap-2 rounded-t-xl border-b border-n-weak px-3 py-3"
                :class="getColumnHeaderClass(column)"
                :style="getColumnHeaderStyle(column)"
              >
                <div class="flex min-w-0 items-center gap-2">
                  <h2 class="truncate text-sm font-medium">
                    {{ getColumnDisplayTitle(column) }}
                  </h2>
                </div>
                <span
                  class="rounded-full px-2 py-0.5 text-xs"
                  :class="getColumnCounterClass(column)"
                >
                  {{ column.totalCount }}
                </span>
              </header>

              <div
                class="flex-1 min-h-0 overflow-y-auto p-2"
                @dragover="event => onColumnDragOver(event, column.id)"
                @drop="event => onColumnDrop(event, column)"
              >
                <div v-if="column.conversations.length" class="space-y-2">
                  <button
                    v-for="conversation in column.conversations"
                    :key="conversation.id"
                    type="button"
                    draggable="true"
                    class="group/kanban-card relative w-full rounded-lg border border-n-weak bg-n-background p-3 text-left transition-colors hover:bg-n-alpha-2"
                    :class="{
                      'cursor-grab': movingConversationId !== conversation.id,
                      'cursor-wait opacity-60':
                        movingConversationId === conversation.id,
                    }"
                    @dragstart="event => onDragStart(event, conversation, column)"
                    @dragend="clearDragState"
                    @contextmenu="event => openConversationContextMenu(event, conversation.id)"
                    @click="onConversationCardClick(conversation.id)"
                  >
                    <div class="flex items-start gap-1">
                      <div class="relative">
                        <Avatar
                          :name="conversation.senderName"
                          :src="conversation.senderThumbnail"
                          :size="32"
                          :status="conversation.senderAvailabilityStatus"
                          hide-offline-status
                          rounded-full
                        />
                      </div>

                      <div class="relative min-w-0 flex-1">
                        <div
                          v-if="conversation.assignees.length"
                          class="mx-2 flex min-w-0 items-center gap-1"
                        >
                          <span
                            class="inline-flex max-w-full items-center truncate px-0 py-0.5 text-xs font-medium leading-3 text-n-slate-11"
                          >
                            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
                            {{ getConversationAssigneeLabel(conversation) }}
                          </span>
                        </div>

                        <h4
                          class="conversation--user my-0 mx-2 min-w-0 flex-1 truncate whitespace-nowrap pt-0.5 text-sm capitalize text-n-slate-12 ltr:pr-16 rtl:pl-16"
                          :class="hasUnreadMessages(conversation) ? 'font-semibold' : 'font-medium'"
                        >
                          {{ conversation.senderName }}
                        </h4>

                        <div
                          class="absolute top-0 flex flex-col ltr:right-0 rtl:left-0"
                        >
                          <span class="ml-auto font-normal leading-4 text-xxs">
                            <TimeAgo
                              :last-activity-timestamp="conversation.lastActivityAt"
                              :created-at-timestamp="conversation.createdAt"
                              :conversation-id="conversation.id"
                            />
                          </span>
                          <span
                            v-if="hasUnreadMessages(conversation)"
                            class="mt-1 h-4 min-w-[1rem] rounded-full bg-n-teal-9 px-1 py-0 text-center text-xxs font-semibold leading-4 text-white shadow-lg ltr:ml-auto rtl:mr-auto"
                          >
                            {{ getUnreadCountLabel(conversation.unreadCount) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <MessagePreview
                      v-if="conversation.lastMessage"
                      :message="conversation.lastMessage"
                      class="my-0 mx-2 h-6 min-w-0 flex-1 text-sm leading-6"
                      :class="getMessagePreviewClass(conversation)"
                    />
                    <p
                      v-else
                      class="my-0 mx-2 h-6 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-6 text-n-slate-11"
                    >
                      <fluent-icon
                        size="16"
                        class="-mt-0.5 inline-block align-middle text-n-slate-10"
                        icon="info"
                      />
                      <span class="mx-0.5">{{ t('CHAT_LIST.NO_MESSAGES') }}</span>
                    </p>

                    <div class="mt-2 flex flex-wrap items-center gap-2">
                      <span
                        class="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                        :class="getConversationStatusBadgeClass(conversation.status)"
                      >
                        {{ getConversationStatusLabel(conversation.status) }}
                      </span>
                      <span
                        v-if="conversation.status === 'snoozed'"
                        class="px-2 inline-flex items-center gap-1 text-[10px] text-n-amber-11"
                      >
                        <Icon icon="i-lucide-alarm-clock" class="size-3" />
                        {{ getSnoozedConditionLabel(conversation) }}
                      </span>
                      <span
                        v-if="conversation.priority"
                        class="inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-0.5 text-[10px] font-medium text-n-slate-11"
                      >
                        <CardPriorityIcon
                          :priority="conversation.priority"
                          class="[&>svg]:size-3"
                        />
                        {{ getPriorityLabel(conversation.priority) }}
                      </span>
                    </div>
                    <CardLabels
                      v-if="conversation.labels.length"
                      :conversation-labels="conversation.labels"
                      class="mt-1"
                    />
                  </button>
                </div>

                <p
                  v-else
                  class="px-2 py-5 text-center text-xs font-medium text-n-slate-10"
                >
                  {{ t('KANBAN_VIEW.EMPTY_COLUMN') }}
                </p>

                <div
                  v-if="column.hasMore || column.isLoadingMore"
                  class="mt-3 flex justify-center"
                >
                  <NextButton
                    sm
                    faded
                    slate
                    :is-loading="column.isLoadingMore"
                    :disabled="column.isLoadingMore"
                    @click="loadMoreConversations(column.id)"
                  >
                    {{ t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
                  </NextButton>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>

    <ContextMenu
      v-if="isConversationContextMenuOpen && contextMenuConversation"
      :x="contextMenuState.x"
      :y="contextMenuState.y"
      @close="closeConversationContextMenu"
    >
      <ConversationContextMenu
        :status="contextMenuConversation.status"
        :inbox-id="contextMenuConversation.inboxId"
        :priority="contextMenuConversation.priority"
        :chat-id="contextMenuConversation.id"
        :has-unread-messages="contextMenuConversation.unreadCount > 0"
        :conversation-labels="contextMenuConversation.labels"
        :conversation-url="getConversationPath(contextMenuConversation.id)"
        :allowed-options="KANBAN_CONTEXT_MENU_ALLOWED_OPTIONS"
        :hidden-status-options="KANBAN_CONTEXT_MENU_HIDDEN_STATUSES"
        @update-conversation="updateConversationStatusFromContextMenu"
        @assign-priority="assignConversationPriority"
        @mark-as-unread="markConversationAsUnread"
        @mark-as-read="markConversationAsRead"
        @assign-agent="assignConversationAgent"
        @assign-team="assignConversationTeam"
        @delete-conversation="deleteConversationFromContextMenu"
        @close="closeConversationContextMenu"
      />
    </ContextMenu>

    <CmdBarConversationSnooze />

    <woot-modal
      v-model:show="isViewEditorOpen"
      size="medium"
      :on-close="closeViewEditor"
    >
      <section
        v-if="isViewEditorOpen"
        class="w-full max-h-[82vh] overflow-y-auto overflow-x-hidden rounded-xl border border-n-weak bg-n-surface-1 p-4 md:p-5"
      >
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ viewEditorTitle }}
        </h2>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ viewEditorDescription }}
        </p>

        <div class="mt-4 grid min-w-0 gap-4 md:grid-cols-2">
          <label class="grid min-w-0 gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('KANBAN_VIEW.VIEW_EDITOR.BOARD_NAME_LABEL') }}
            </span>
            <NextInput
              v-model="viewForm.name"
              :placeholder="t('KANBAN_VIEW.VIEW_EDITOR.BOARD_NAME_PLACEHOLDER')"
            />
          </label>

          <label class="grid min-w-0 gap-1">
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('FILTER.ATTRIBUTES.STATUS') }}
            </span>
            <MultiSelect
              v-model="selectedStatusOptions"
              :options="statusOptions"
              full-width
            />
          </label>
        </div>

        <div class="mt-4 grid min-w-0 gap-3 lg:grid-cols-[1fr_0.95fr]">
          <div class="min-w-0 rounded-lg border border-n-weak bg-n-surface-1 p-2.5">
            <h3 class="text-xs font-medium text-n-slate-12">
              {{ t('FILTER.ATTRIBUTES.LABELS') }}
            </h3>

            <p
              v-if="!availableColumnsForForm.length"
              class="mt-2 text-xs text-n-slate-11"
            >
              {{ t('KANBAN_VIEW.VIEW_EDITOR.ALL_LABELS_SELECTED') }}
            </p>

            <div v-else class="mt-2 flex max-h-56 flex-wrap gap-2 overflow-y-auto pr-1">
              <NextButton
                v-for="column in availableColumnsForForm"
                :key="column.id"
                xs
                faded
                slate
                @click="addColumnToViewForm(column.title)"
              >
                #{{ column.title }}
              </NextButton>
            </div>
          </div>

          <div class="min-w-0 rounded-lg border border-n-weak bg-n-surface-1 p-2.5">
            <h3 class="text-xs font-medium text-n-slate-12">
              {{ t('KANBAN_VIEW.VIEW_EDITOR.COLUMNS_TITLE') }}
            </h3>

            <p
              v-if="!viewForm.columns.length"
              class="mt-2 text-xs text-n-slate-11"
            >
              {{ t('KANBAN_VIEW.VIEW_EDITOR.COLUMNS_EMPTY') }}
            </p>

            <ul v-else class="mt-2 grid max-h-56 gap-2 overflow-y-auto pr-1">
              <li
                v-for="(columnTitle, index) in viewForm.columns"
                :key="`${columnTitle}-${index}`"
                class="flex min-w-0 items-center justify-between gap-2 rounded-md border border-n-weak px-2 py-1"
              >
                <span class="truncate text-xs font-medium text-n-slate-12">
                  #{{ columnTitle }}
                </span>
                <div class="shrink-0 flex items-center gap-1">
                  <NextButton
                    xs
                    faded
                    slate
                    icon="i-lucide-arrow-up"
                    :disabled="index === 0"
                    @click="moveColumnInViewForm(index, -1)"
                  />
                  <NextButton
                    xs
                    faded
                    slate
                    icon="i-lucide-arrow-down"
                    :disabled="index === viewForm.columns.length - 1"
                    @click="moveColumnInViewForm(index, 1)"
                  />
                  <NextButton
                    xs
                    faded
                    ruby
                    icon="i-lucide-trash-2"
                    @click="removeColumnFromViewForm(index)"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="mt-5 flex items-center justify-end gap-2">
          <NextButton sm faded slate @click="closeViewEditor">
            {{ t('FILTER.CANCEL_BUTTON_LABEL') }}
          </NextButton>
          <NextButton
            sm
            solid
            blue
            :disabled="isViewFormInvalid || isSavingView"
            :is-loading="isSavingView"
            @click="saveView"
          >
            {{ t('KANBAN_VIEW.VIEW_EDITOR.SAVE') }}
          </NextButton>
        </div>
      </section>
    </woot-modal>

    <woot-delete-modal
      v-if="showDeleteViewModal"
      v-model:show="showDeleteViewModal"
      :on-close="closeDeleteViewModal"
      :on-confirm="deleteCurrentView"
      :title="t('FILTER.CUSTOM_VIEWS.DELETE.MODAL.CONFIRM.TITLE')"
      :message="t('FILTER.CUSTOM_VIEWS.DELETE.MODAL.CONFIRM.MESSAGE')"
      :message-value="activeDeleteViewLabel"
      :confirm-text="t('FILTER.CUSTOM_VIEWS.DELETE.MODAL.CONFIRM.YES')"
      :reject-text="t('FILTER.CUSTOM_VIEWS.DELETE.MODAL.CONFIRM.NO')"
    />
  </section>
</template>
